import React, { useMemo, useState } from "react";
import { useRefinementList } from "react-instantsearch-hooks-web";
import BrandLogo from "./BrandLogo";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";

export default function BrandFilter() {
  const { theme: themeObj, isForest } = useTheme();
  const { t } = useTranslation();
  const { items, refine, canRefine } = useRefinementList({
    attribute: "data.brand",
    operator: "or",
    limit: 50,
    sortBy: ["name:asc"],
  });

  const [open, setOpen] = useState(false);
  // local search state for filtering brand list
  const [brandSearch, setBrandSearch] = useState("");

  const selected = useMemo(() => items.filter(i => i.isRefined), [items]);

  // computed filtered list based on search query
  const filteredItems = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i => i.label.toLowerCase().includes(q));
  }, [items, brandSearch]);

  const currentLabel =
    selected.length === 1 ? selected[0].label :
      selected.length > 1 ? t('home.brandsCount', { count: selected.length }) :
        t('home.brand');

  // Theme-based styles
  const buttonStyle = {
    backgroundColor: isForest ? 'rgba(6,78,59,0.4)' : themeObj.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : themeObj.surface.cardBorder}`,
    color: isForest ? '#e8f5e9' : themeObj.text.primary,
  };

  const dropdownStyle = {
    backgroundColor: isForest ? '#0f3a30' : themeObj.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : themeObj.surface.cardBorder}`,
    boxShadow: isForest ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.15)',
  };

  const inputStyle = {
    backgroundColor: isForest ? 'rgba(6,78,59,0.6)' : themeObj.input.background,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : themeObj.input.border}`,
    color: isForest ? '#e8f5e9' : themeObj.text.primary,
  };

  const itemBaseStyle = (isRefined) => ({
    backgroundColor: isRefined
      ? (isForest ? 'rgba(6,78,59,0.6)' : themeObj.navigation.itemBackgroundActive)
      : 'transparent',
    color: isForest ? '#e8f5e9' : themeObj.text.primary,
    boxShadow: isRefined ? (isForest ? '0 0 6px 1px rgba(76,175,80,0.6)' : '0 0 6px 1px rgba(76,175,80,0.3)') : 'none',
    border: isRefined ? `2px solid ${isForest ? '#4caf50' : themeObj.interactive.primary}` : '2px solid transparent',
  });

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl transition-colors"
        style={buttonStyle}
        disabled={!canRefine}
      >
        <span className="text-sm">{currentLabel}</span>
      </button>

      {open && (
        <div
          className="absolute z-20 mt-2 w-full max-h-72 overflow-auto rounded-xl"
          style={dropdownStyle}
          onMouseLeave={() => setOpen(false)}
        >
          {/* Search input pinned at top of dropdown */}
          <div className="p-2 sticky top-0" style={{ backgroundColor: isForest ? '#0f3a30' : themeObj.surface.card }}>
            <input
              type="text"
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              placeholder={t('home.searchBrands')}
              className="w-full rounded-md px-3 py-2 text-sm focus:outline-none"
              style={inputStyle}
            />
          </div>

          <ul className="divide-y" style={{ borderColor: isForest ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
            {filteredItems.map(item => (
              <li key={item.label} className="px-1 py-[1px]">
                <button
                  className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg relative transition-all duration-150"
                  style={itemBaseStyle(item.isRefined)}
                  onClick={() => refine(item.value)}
                  onMouseEnter={(e) => {
                    if (!item.isRefined) {
                      e.currentTarget.style.backgroundColor = isForest ? 'rgba(6,78,59,0.4)' : 'rgba(0,0,0,0.03)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!item.isRefined) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <span className="truncate">{item.label}</span>
                  <BrandLogo label={item.label} variant="noText" className="w-5 h-5" />
                </button>
              </li>
            ))}
            {filteredItems.length === 0 && (
              <li className="px-3 py-2 text-sm" style={{ color: isForest ? '#a5d6a7' : themeObj.text.tertiary }}>{t('home.noBrands')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
